import React, { useCallback, useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import CircularProgress from '@mui/material/CircularProgress'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import IconButton from '@mui/material/IconButton'
import DeleteIcon from '@mui/icons-material/Delete'
import LogoutIcon from '@mui/icons-material/Logout'
import api from 'services/api'
import useToast from 'hooks/useToast'
import useAuthActions from 'store/actions/auth'

const Settings = () => {
  const toast = useToast()
  const { logout } = useAuthActions()

  const [loading, setLoading] = useState(true)
  const [linked, setLinked] = useState(false)
  const [displayName, setDisplayName] = useState(null)
  const [subscriptions, setSubscriptions] = useState([])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const status = await api.slack.getStatus()
      setLinked(status.linked)
      setDisplayName(status.slack_display_name)
      if (status.linked) {
        setSubscriptions(await api.slack.getSubscriptions())
      } else {
        setSubscriptions([])
      }
    } catch (err) {
      toast({ message: err.message, severity: 'error' })
    } finally {
      setLoading(false)
    }
  }, [toast])

  useEffect(() => {
    load()
  }, [load])

  const connect = useCallback(async () => {
    try {
      const { authorize_url } = await api.slack.authorize(null)
      window.location.assign(authorize_url)
    } catch (err) {
      toast({ message: err.message, severity: 'error' })
    }
  }, [toast])

  const disconnect = useCallback(async () => {
    try {
      await api.slack.disconnect()
      setLinked(false)
      setDisplayName(null)
      setSubscriptions([])
      toast({ message: 'Slack disconnected.' })
    } catch (err) {
      toast({ message: err.message, severity: 'error' })
    }
  }, [toast])

  const unsubscribe = useCallback(
    async (channelCid) => {
      try {
        const updated = await api.slack.unsubscribe(channelCid)
        setSubscriptions(updated)
        toast({ message: 'Unsubscribed.' })
      } catch (err) {
        toast({ message: err.message, severity: 'error' })
      }
    },
    [toast]
  )

  return (
    <Box sx={{ padding: 4, maxWidth: 640, margin: '40px auto' }}>
      <Typography variant="h4" sx={{ marginBottom: 3 }}>
        Settings
      </Typography>

      <Paper sx={{ padding: 3 }}>
        <Typography variant="h6" sx={{ marginBottom: 2 }}>
          Slack
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {linked ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography>
                  Connected
                  {displayName ? ` as ${displayName}` : ''}
                </Typography>
                <Button color="error" variant="outlined" onClick={disconnect}>
                  Disconnect
                </Button>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography color="text.secondary">
                  Not connected. Connect Slack to get notified about Team Reclip
                  conversations.
                </Typography>
                <Button variant="contained" onClick={connect}>
                  Connect with Slack
                </Button>
              </Box>
            )}

            {linked && (
              <>
                <Divider sx={{ marginY: 3 }} />
                <Typography variant="subtitle1" sx={{ marginBottom: 1 }}>
                  Subscriptions
                </Typography>
                {subscriptions.length === 0 ? (
                  <Typography color="text.secondary">
                    You aren't subscribed to any conversations. Use the bell in
                    a conversation header to subscribe.
                  </Typography>
                ) : (
                  <List dense>
                    {subscriptions.map((cid) => (
                      <ListItem
                        key={cid}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            title="Unsubscribe"
                            onClick={() => unsubscribe(cid)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        }
                      >
                        <ListItemText primary={cid} />
                      </ListItem>
                    ))}
                  </List>
                )}
              </>
            )}
          </>
        )}
      </Paper>

      <Paper sx={{ padding: 3, marginTop: 3 }}>
        <Typography variant="h6" sx={{ marginBottom: 2 }}>
          Account
        </Typography>
        <Button
          color="error"
          variant="outlined"
          startIcon={<LogoutIcon />}
          onClick={logout}
        >
          Log Out
        </Button>
      </Paper>
    </Box>
  )
}

export default Settings
